import React, { useState } from "react";

import { ReactComponent as PasswordResetAttentionIcon } from "../../../assets/svg/password_reset_attention.svg";
import { ReactComponent as CheckCircleIcon } from "../../../assets/svg/check-circle.svg";
import { IClass, StudentTemporaryPassword } from "../../../state";
import ReissueStudentPasswordModal from "../../../organisms/teacher/ReissueStudentPasswordModal";
import DropdownMenu, { Option } from "../../../atoms/DropdownMenu";

import styles from "./StudentTemporaryPasswordList.module.scss";

const SORT_TYPE = {
  Kana: 1,
  StudentNumber: 2,
};

const OPTIONS: Option[] = [
  { label: "50音順", value: SORT_TYPE.Kana },
  { label: "学籍番号順", value: SORT_TYPE.StudentNumber },
];

type Props = {
  students: StudentTemporaryPassword[];
  classes: IClass[];
  classId: number;
  calling: boolean;
  onReissueStudentPassword: (student_id: number) => void;
  setClassId: (id: number) => void;
};

export const StudentTemporaryPasswordList = ({
  students,
  classes,
  classId,
  calling,
  onReissueStudentPassword,
  setClassId,
}: Props) => {
  const classOption: Option[] = classes.map(c => {
    return { label: c.full_name, value: c.id };
  });

  const [sortType, setSortType] = useState(OPTIONS[0]);
  const [
    reissuePasswordStudent,
    setReissuePasswordStudent,
  ] = useState<StudentTemporaryPassword | null>(null);

  const sortedStudents = (() => {
    switch (sortType.value) {
      case SORT_TYPE.Kana:
        return [...students].sort((x, y) =>
          `${x.last_name_kana}${x.first_name_kana}`.localeCompare(
            `${y.last_name_kana}${y.first_name_kana}`,
            "ja",
          ),
        );
      case SORT_TYPE.StudentNumber:
        return [...students].sort((x, y) => {
          const collator = new Intl.Collator(["ja", "en"], { numeric: true });

          const a = x.student_no_in_school ?? "";
          const b = y.student_no_in_school ?? "";

          if (!a) return 1;
          if (!b) return -1;

          return collator.compare(a, b);
        });
      default:
        return students;
    }
  })();

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.titleContainer}>
          <PasswordResetAttentionIcon />
          <p>
            生徒がパスワードを忘れた場合は以下の手順でパスワードを再発行してください
          </p>
        </div>
        {classId ? (
          <div className={styles.tableContainer}>
            <div className={styles.procedure}>
              <ol>
                <li>
                  パスワードを再発行したい生徒の[パスワード再発行]をクリックします。
                </li>
                <li>表示された[仮パスワード]を生徒に共有します。</li>
              </ol>
              <p className={styles.note}>
                ログインで授業開始が大幅に遅れそうな場合は、回答をメモしておき、授業後にデジタル教材へ入力することをおすすめします。
              </p>
            </div>
            <div className={styles.tableTitleContainer}>
              <p>ログインID/仮パスワード一覧</p>
              <div className={styles.dropdownContainer}>
                {classes.length > 1 && (
                  <DropdownMenu
                    options={classOption}
                    defaultValue={classOption.find(c => c.value === classId)}
                    onChange={value => {
                      setClassId(value);
                    }}
                    className={styles.dropdown}
                    backdrop
                  />
                )}
                <DropdownMenu
                  options={OPTIONS}
                  defaultValue={sortType}
                  onChange={value => {
                    const selectSort = OPTIONS.find(
                      option => option.value === value,
                    ) as Option;
                    setSortType(selectSort);
                  }}
                  className={styles.dropdown}
                  backdrop
                />
              </div>
            </div>
            {sortedStudents.length > 0 ? (
              <div className={styles.tableWrapper}>
                <table>
                  <thead>
                    <tr>
                      <th>名前</th>
                      <th>学籍番号</th>
                      <th>ログインID</th>
                      <th>仮パスワード</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStudents.map(s => (
                      <tr key={s.id}>
                        <td>{s.full_name}</td>
                        <td>{s.student_no_in_school}</td>
                        <td>{s.identity}</td>
                        {s.temporary_password ? (
                          <td>{s.temporary_password}</td>
                        ) : (
                          <td>
                            <div className={styles.passwordRegistered}>
                              <CheckCircleIcon />
                              <p>生徒が設定済み</p>
                            </div>
                            <button
                              className={styles.reissuePassword}
                              onClick={() => setReissuePasswordStudent(s)}
                            >
                              パスワード再発行
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !calling && (
                <div>
                  <p className={styles.emptyMessage}>
                    担当しているクラスに生徒が登録されていません。
                  </p>
                </div>
              )
            )}
          </div>
        ) : (
          <div className={styles.emptyMessageContainer}>
            <p className={styles.emptyMessage}>
              担当しているクラスがありません。
            </p>
          </div>
        )}
      </div>
      {reissuePasswordStudent && (
        <ReissueStudentPasswordModal
          student_name={reissuePasswordStudent.full_name}
          onClose={() => setReissuePasswordStudent(null)}
          onReissueStudentPassword={() =>
            onReissueStudentPassword(reissuePasswordStudent.id)
          }
        />
      )}
    </>
  );
};

export default StudentTemporaryPasswordList;
